import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiBaseService } from './api-base.service';

@Injectable({
  providedIn: 'root',
})
export class TemplatesService {
  // State management for area types
  public areaTypes$ = new BehaviorSubject<any[]>([]);
  private isAreaTypesLoading = false;

  // State management for project folders
  public projectFolders$ = new BehaviorSubject<any[]>([]);
  private isProjectFoldersLoading = false;

  // State management for grid folders
  public gridFolders$ = new BehaviorSubject<any[]>([]);
  private isGridFoldersLoading = false;

  constructor(private apiService: ApiBaseService) {}

  async getTaskConfigList(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetTaskConfigList', payload, true, true);
  }

  async importConfigFromCsv(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/ImportConfigFromCsv', payload, true, true);
  }

  async getProjectRepoStructure(payload: any): Promise<any> {
    const result = await this.apiService.httpRequest('TemplatesService/GetProjectRepoStructure', payload, true, true);

    // Update project folders state if available
    if (!result.hasError && result.items) {
      this.projectFolders$.next(result.items);
    }

    return result;
  }

  async loadProjectFolders(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.projectFolders$.value.length > 0) {
      return this.projectFolders$.value;
    }

    if (this.isProjectFoldersLoading) {
      return this.projectFolders$.value;
    }

    this.isProjectFoldersLoading = true;
    try {
      const result = await this.getProjectRepoStructure({});
      return this.projectFolders$.value;
    } finally {
      this.isProjectFoldersLoading = false;
    }
  }

  async loadGridFolders(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.gridFolders$.value.length > 0) {
      return this.gridFolders$.value;
    }

    if (this.isGridFoldersLoading) {
      return this.gridFolders$.value;
    }

    this.isGridFoldersLoading = true;
    try {
      const result = await this.getGridConnectionRepoStructure({});
      return this.gridFolders$.value;
    } finally {
      this.isGridFoldersLoading = false;
    }
  }

  // Brandon Calitz api
  async GetProjectForFilter(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetProjectForFilter', payload, true, true);
  }

  async GetGridConnectionForFilter(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetGridConnectionForFilter', payload, true, true);
  }

  async getGridConnectionRepoStructure(payload: any): Promise<any> {
    const result = await this.apiService.httpRequest(
      'TemplatesService/GetGridConnectionRepoStructure',
      payload,
      true,
      true
    );

    // Update grid folders state if available
    if (!result.hasError && result.items) {
      this.gridFolders$.next(result.items);
    }

    return result;
  }

  async getDocumentFolders(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetDocumentFolders', payload, true, true);
  }

  async writeGridConnectionRepoFolder(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/WriteGridConnectionRepoFolder', payload, true, true);
  }

  async writeProjectRepoFolder(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/WriteProjectRepoFolder', payload, true, true);
  }

  async getDocumentFolderDeleteInfo(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetDocumentFolderDeleteInfo', payload, true, true);
  }

  async deleteDocumentFolder(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/DeleteDocumentFolder', payload, true, true);
  }

  async getFolderSettings(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetFolderSettings', payload, true, true);
  }

  async saveFolderSettings(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/SaveFolderSettings', payload, true, true);
  }

  async uploadConfigTaskTemplateFile(payload: any): Promise<any> {
    return this.apiService.httpRequestForm('TemplatesService/UploadConfigTaskTemplateFile', payload, true, true);
  }

  async uploadMicroTaskTemplateFile(payload: any): Promise<any> {
    return this.apiService.httpRequestForm('TemplatesService/UploadMicroTaskTemplateFile', payload, true, true);
  }

  async deleteTemplateFile(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/DeleteTemplateFile', payload, true, true);
  }

  async deleteMicrotaskTemplateFile(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/DeleteMicrotaskTemplateFile', payload, true, true);
  }

  async getMilestones(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetMilestones', payload, true, true);
  }

  async getAreaTypes(): Promise<any> {
    const result = await this.apiService.httpRequest('TemplatesService/GetAreaTypes', {}, true, true);

    // Update area types state if available
    if (!result.hasError && result.areaTypes) {
      this.areaTypes$.next(result.areaTypes);
    }

    return result;
  }

  async writeProjectAreaType(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/WriteProjectAreaType', payload, true, true);
  }

  async getRemunerationTags(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetRemunerationTags', payload, true, true);
  }

  async writeRemunerationTag(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/WriteRemunerationTag', payload, true, true);
  }

  async writeProjectTaskConfigTask(payload: any, templateFile: File | null = null): Promise<any> {
    const formData = new FormData();

    // Append the data field as JSON string
    formData.append('data', JSON.stringify(payload));

    // Append files with their corresponding keys (file IDs)
    if (templateFile) {
      formData.append('TemplateFile', templateFile, templateFile.name);
    }
    return this.apiService.httpRequestForm('TemplatesService/WriteProjectTaskConfigTask', formData, true, true);
  }
  async getTemplateApplyStatus(): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetTemplateApplyStatus', {}, true, false);
  }

  async retryTemplateApplyJob(jobId: number): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/RetryTemplateApplyJob', { jobId }, true, false);
  }

  async dismissTemplateApplyJob(jobId: number): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/DismissTemplateApplyJob', { jobId }, true, false);
  }
  async writeTaskConfigList(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/WriteTaskConfigList', payload, true, true);
  }

  async getTemplateFile(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetTemplateFile', payload, true, true);
  }
  async getProjectTaskConfigList(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetProjectTaskConfigList', payload, true, true);
  }

  async reorderTaskConfigList(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/ReorderTaskConfigList', payload, true, true);
  }

  async loadAreaTypes(forceReload: boolean = false): Promise<any[]> {
    if (!forceReload && this.areaTypes$.value.length > 0) {
      return this.areaTypes$.value;
    }

    if (this.isAreaTypesLoading) {
      return this.areaTypes$.value;
    }

    this.isAreaTypesLoading = true;
    try {
      const result = await this.getAreaTypes();
      return this.areaTypes$.value;
    } finally {
      this.isAreaTypesLoading = false;
    }
  }

  async moveTasksToTaskPackage(payload: any): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/MoveTasksToTaskPackage', payload, true, true);
  }

  async getArchiveImpact(configId: number): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/GetArchiveImpact', { configId }, true, true);
  }

  async archiveTaskConfig(configId: number, setOpenTasksNotRequired = false): Promise<any> {
    return this.apiService.httpRequest(
      'TemplatesService/ArchiveTaskConfig',
      { configId, setOpenTasksNotRequired },
      true,
      true
    );
  }

  async unarchiveTaskConfig(configId: number): Promise<any> {
    return this.apiService.httpRequest('TemplatesService/UnarchiveTaskConfig', { configId }, true, true);
  }
}
